import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bookingsAPI, matchesAPI, seatsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// ─── 1. BULLETPROOF LOGO PATH ───
const logoPath = (shortName) => `${window.location.origin}/assets/assets/${shortName?.toLowerCase()}.png`;

function TeamLogo({ shortName, size = 48, color = '#00E5FF' }) {
  const [imgError, setImgError] = useState(false);

  if (imgError || !shortName) {
    return (
      <div style={{
        width: size, height: size, borderRadius: '50%', background: `${color}20`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'Bebas Neue, sans-serif', fontSize: size * 0.45, color: color
      }}>
        {shortName?.slice(0, 2)}
      </div>
    );
  }

  return (
    <img
      src={logoPath(shortName)}
      alt={shortName}
      style={{ width: size, height: size, objectFit: 'contain', display: 'block' }}
      onError={() => setImgError(true)}
    />
  );
}

// ─── 2. COLOR PALETTE ───
const STAND_COLORS = [
  '#CD7F32', // Bronze  - General / cheapest
  '#C0C0C0', // Silver
  '#FFD700', // Gold
  '#E5C100', // Premium Gold
  '#00D4FF', // Cyan - VIP
  '#A259FF', // Purple - Platinum
  '#FF6B00', // Orange - Corporate / most expensive
];

const getCatColor = (index, total) => {
  // Map index across available colors proportionally
  const colorIndex = Math.round((index / Math.max(total - 1, 1)) * (STAND_COLORS.length - 1));
  return STAND_COLORS[Math.min(colorIndex, STAND_COLORS.length - 1)];
};

// ─── 3. DYNAMIC STADIUM MAP ───
// Builds arc segments dynamically based on number of categories.
// Outer rings = cheap seats, inner rings = expensive seats.
function buildSegments(numCats) {
  if (numCats === 0) return [];

  const CX = 250, CY = 250;
  const MIN_R = 18, MAX_R = 238;
  const GAP = 3; // gap between rings in px

  const totalRange = MAX_R - MIN_R;
  // Give outer (cheaper) rings more space, inner (expensive) rings less
  const rings = numCats;
  const ringHeight = (totalRange - GAP * (rings - 1)) / rings;

  const segments = [];

  for (let i = 0; i < numCats; i++) {
    // i=0 is outermost (cheapest), i=numCats-1 is innermost (most expensive)
    const r2 = MAX_R - i * (ringHeight + GAP);
    const r1 = r2 - ringHeight;

    // Segment count per ring: outer rings get more segments (more seats visible)
    const segCount = i < 2 ? 5 : i < 4 ? 4 : i < 6 ? 3 : 2;
    const angleStep = 360 / segCount;

    for (let j = 0; j < segCount; j++) {
      const startDeg = -180 + j * angleStep;
      const endDeg = startDeg + angleStep;
      segments.push({
        id: `cat-${i}-seg-${j}`,
        catIndex: i,
        s: startDeg,
        e: endDeg,
        r1: Math.max(MIN_R, r1),
        r2: r2,
      });
    }
  }

  return segments;
}

function arcPath(cx, cy, r1, r2, startDeg, endDeg) {
  const toRad = d => d * Math.PI / 180;
  const GAP_DEG = 0.8;
  const s = startDeg + GAP_DEG, e = endDeg - GAP_DEG;
  const large = (e - s) > 180 ? 1 : 0;
  const sr = toRad(s), er = toRad(e);
  const x1 = cx + r2 * Math.cos(sr), y1 = cy + r2 * Math.sin(sr);
  const x2 = cx + r2 * Math.cos(er), y2 = cy + r2 * Math.sin(er);
  const x3 = cx + r1 * Math.cos(er), y3 = cy + r1 * Math.sin(er);
  const x4 = cx + r1 * Math.cos(sr), y4 = cy + r1 * Math.sin(sr);
  return `M${x1.toFixed(2)},${y1.toFixed(2)} A${r2},${r2} 0 ${large},1 ${x2.toFixed(2)},${y2.toFixed(2)} L${x3.toFixed(2)},${y3.toFixed(2)} A${r1},${r1} 0 ${large},0 ${x4.toFixed(2)},${y4.toFixed(2)} Z`;
}

function StadiumMap({ cats, selectedCatIndex, onSelectCat }) {
  const CX = 250, CY = 250;
  const [hoveredCat, setHoveredCat] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, text: '' });

  const segments = buildSegments(cats.length);
  const total = cats.length;

  const handleMouseMove = (e, catIndex) => {
    const svg = e.currentTarget.closest('svg');
    const rect = svg.getBoundingClientRect();
    setTooltip({
      visible: true,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top - 14,
      text: cats[catIndex]?.name || '',
    });
  };

  const handleMouseLeave = () => {
    setHoveredCat(null);
    setTooltip({ ...tooltip, visible: false });
  };

  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: 400, margin: '0 auto' }}>
      <svg
        viewBox="0 0 500 500"
        style={{ width: '100%', display: 'block' }}
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Background */}
        <ellipse cx={CX} cy={CY} rx="245" ry="245" fill="#030308" stroke="#0A0D1A" strokeWidth="2" />

        {/* Pitch label */}
        <text x={CX} y={CY + 5} textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="11" fontFamily="Rajdhani, sans-serif">PITCH</text>

        {segments.map(seg => {
          const cat = cats[seg.catIndex];
          const sold = !cat || cat.availableSeats === 0;
          const isSel = selectedCatIndex === seg.catIndex;
          const isHov = hoveredCat === seg.catIndex;
          const color = getCatColor(seg.catIndex, total);

          const fill = sold ? '#0A0D1A' : color;
          const opacity = sold ? 0.6 : isSel ? 1 : isHov ? 0.6 : 0.2;

          return (
            <path
              key={seg.id}
              d={arcPath(CX, CY, seg.r1, seg.r2, seg.s, seg.e)}
              fill={fill}
              fillOpacity={opacity}
              stroke={isSel ? '#fff' : isHov ? color : sold ? '#0A0D1A' : color}
              strokeWidth={isSel ? '2' : isHov ? '1.5' : '0.5'}
              strokeOpacity={isSel ? 1 : isHov ? 0.8 : 0.4}
              style={{ cursor: sold ? 'not-allowed' : 'pointer', transition: 'all 0.15s' }}
              onClick={() => !sold && onSelectCat(seg.catIndex)}
              onMouseEnter={() => setHoveredCat(seg.catIndex)}
              onMouseMove={(e) => handleMouseMove(e, seg.catIndex)}
              onMouseLeave={handleMouseLeave}
            />
          );
        })}

        {/* Center pitch circle */}
        <ellipse cx={CX} cy={CY} rx="15" ry="15" fill="#003366" opacity="0.9" />
        <rect x={CX - 4} y={CY - 12} width="8" height="24" rx="1" fill="#00E5FF" />
      </svg>

      {/* Tooltip */}
      {tooltip.visible && tooltip.text && (
        <div style={{
          position: 'absolute',
          left: tooltip.x,
          top: tooltip.y,
          transform: 'translate(-50%, -100%)',
          background: 'rgba(10,13,26,0.95)',
          border: '1px solid rgba(0,229,255,0.3)',
          borderRadius: 6,
          padding: '5px 10px',
          fontSize: 12,
          fontFamily: 'Rajdhani, sans-serif',
          color: '#fff',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          zIndex: 10,
          letterSpacing: '0.04em',
        }}>
          {tooltip.text}
        </div>
      )}

      {/* Ring legend: outermost = cheapest label */}
      {cats.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, padding: '0 8px' }}>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontFamily: 'Rajdhani' }}>← Outer = General</span>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontFamily: 'Rajdhani' }}>Inner = VIP →</span>
        </div>
      )}
    </div>
  );
}

// ─── 4. MAIN PAGE COMPONENT ───
export default function SeatSelectionPage() {
  const { id: matchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCatIndex, setSelectedCat] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [holding, setHolding] = useState(false);

  const listRefs = useRef([]);
  const summaryRef = useRef(null);

  useEffect(() => {
    matchesAPI.getById(matchId)
      .then(r => setMatch(r.data))
      .catch(() => toast.error('Failed to load match'))
      .finally(() => setLoading(false));
  }, [matchId]);

  const handleSelectCat = (index) => {
    setSelectedCat(index);
    if (window.innerWidth <= 900 && summaryRef.current) {
      setTimeout(() => summaryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } else if (listRefs.current[index]) {
      listRefs.current[index].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleProceed = async () => {
    if (selectedCatIndex === null) { toast.error('Please select a ticket category'); return; }
    if (!user) { toast.error('Please log in to book tickets'); return; }

    setHolding(true);
    try {
      const cat = match.ticketCategories[selectedCatIndex];

      const seatsRes = await seatsAPI.getByMatch(matchId);
      const availableSeats = seatsRes.data
        .filter(s => s.category === cat.name && s.status === 'available')
        .slice(0, quantity);

      if (availableSeats.length < quantity) {
        toast.error('Not enough seats available'); return;
      }

      const seatIds = availableSeats.map(s => s._id);
      await seatsAPI.hold(matchId, seatIds);

      const booking = await bookingsAPI.create({
        matchId,
        seatIds,
        attendees: Array.from({ length: quantity }, (_, i) => ({
          name: user?.name || `Attendee ${i + 1}`
        })),
      });

      navigate(`/checkout/${booking.data._id}`);
    } catch (err) {
      console.error('Booking error:', err);
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Booking failed.');
    } finally {
      setHolding(false);
    }
  };

  if (loading) return (
    <div style={{ background: '#020205', minHeight: '100vh', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Bebas Neue, sans-serif', fontSize: 28, letterSpacing: '0.1em' }}>
      Loading Match...
    </div>
  );

  if (!match) return (
    <div style={{ background: '#020205', minHeight: '100vh', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Bebas Neue, sans-serif', fontSize: 24 }}>
      Match not found.
    </div>
  );

  const cats = match.ticketCategories || [];
  const selCat = selectedCatIndex !== null ? cats[selectedCatIndex] : null;
  const subtotal = selCat ? selCat.price * quantity : 0;
  const fee = Math.round(subtotal * 0.02);
  const grandTotal = subtotal + fee;

  return (
    <div style={{ background: '#020205', minHeight: '100vh', color: '#fff', fontFamily: 'Outfit, sans-serif' }}>
      <style>{`
        * { box-sizing: border-box; }
        .layout-grid { display: grid; grid-template-columns: minmax(0, 1fr) 380px; gap: 30px; padding: 30px 20px; max-width: 1200px; margin: 0 auto; width: 100%; }
        .cat-list-card { background: #0A0D1A; border: 1px solid rgba(255,255,255,0.05); border-radius: 12px; padding: 20px; margin-bottom: 16px; cursor: pointer; transition: all 0.2s ease; position: relative; }
        .cat-list-card:hover:not(.sold-out) { border-color: rgba(0, 229, 255, 0.4); transform: translateY(-2px); }
        .cat-list-card.selected { border-color: #00E5FF; background: rgba(0, 85, 255, 0.05); }
        .qty-btn { width: 36px; height: 36px; border-radius: 6px; border: 1px solid rgba(0, 229, 255, 0.2); background: rgba(0, 85, 255, 0.1); color: #fff; font-size: 20px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .qty-btn:hover:not(:disabled) { background: rgba(0, 229, 255, 0.2); }
        .qty-btn:disabled { opacity: 0.3; cursor: not-allowed; }
        @media (max-width: 900px) {
          .layout-grid { grid-template-columns: 1fr; padding: 15px; gap: 20px; }
          .checkout-panel { position: static !important; width: 100% !important; margin-top: 10px; }
        }
      `}</style>

      {/* ── HEADER ── */}
      <div style={{ background: 'linear-gradient(180deg, #0A0D1A 0%, #020205 100%)', padding: '30px 20px', borderBottom: '1px solid rgba(0, 229, 255, 0.1)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', marginBottom: 20, cursor: 'pointer', fontFamily: 'Rajdhani', fontSize: 16 }}>← Back to Matches</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 30, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <TeamLogo shortName={match.team1?.shortName} size={50} color={match.team1?.color} />
              <div style={{ fontFamily: 'Bebas Neue', fontSize: 36, color: match.team1?.color || '#00E5FF', lineHeight: 1 }}>{match.team1?.shortName}</div>
            </div>
            <div style={{ fontFamily: 'Bebas Neue', fontSize: 24, color: 'rgba(255,255,255,0.5)' }}>VS</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <TeamLogo shortName={match.team2?.shortName} size={50} color={match.team2?.color} />
              <div style={{ fontFamily: 'Bebas Neue', fontSize: 36, color: match.team2?.color || '#0055FF', lineHeight: 1 }}>{match.team2?.shortName}</div>
            </div>
            <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
              <div style={{ fontFamily: 'Rajdhani', fontSize: 14, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.05em' }}>{match.venue?.name}</div>
              <div style={{ fontFamily: 'Rajdhani', fontSize: 13, color: 'rgba(255,255,255,0.3)' }}>{match.venue?.city}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── MAIN GRID ── */}
      <div className="layout-grid">
        {/* LEFT SIDE */}
        <div style={{ minWidth: 0 }}>
          <div style={{ background: '#0A0D1A', borderRadius: 16, padding: '30px 20px', marginBottom: 24, border: '1px solid rgba(255,255,255,0.05)' }}>
            <StadiumMap cats={cats} selectedCatIndex={selectedCatIndex} onSelectCat={handleSelectCat} />
            <div style={{ textAlign: 'center', marginTop: 12, fontSize: 12, color: 'rgba(255,255,255,0.35)', fontFamily: 'Rajdhani', letterSpacing: '0.05em' }}>
              Tap a stand on the map to select · Hover to see stand name
            </div>
          </div>

          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 24, letterSpacing: '0.05em', marginBottom: 16 }}>SELECT CATEGORY</h2>

          <div>
            {cats.map((cat, i) => {
              const isSelected = selectedCatIndex === i;
              const soldOut = cat.availableSeats === 0;
              const color = getCatColor(i, cats.length);
              const pct = Math.min(100, Math.max(0, 100 - (cat.availableSeats / (cat.totalSeats || cat.availableSeats + 1000)) * 100));

              return (
                <div
                  key={i}
                  ref={el => listRefs.current[i] = el}
                  className={`cat-list-card ${isSelected ? 'selected' : ''} ${soldOut ? 'sold-out' : ''}`}
                  onClick={() => !soldOut && handleSelectCat(i)}
                  style={{ opacity: soldOut ? 0.5 : 1, borderColor: isSelected ? color : undefined }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      {/* Color swatch matching map */}
                      <div style={{ width: 14, height: 14, borderRadius: 3, background: color, marginTop: 3, flexShrink: 0, opacity: soldOut ? 0.4 : 1 }} />
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'Rajdhani', color: isSelected ? color : '#fff', letterSpacing: '0.04em' }}>{cat.name}</div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 3 }}>{soldOut ? 'Sold Out' : `${cat.availableSeats.toLocaleString()} available`}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                      <div style={{ fontFamily: 'Bebas Neue', fontSize: 22, lineHeight: 1, color: isSelected ? color : '#fff' }}>₹{cat.price?.toLocaleString()}</div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 3 }}>per ticket</div>
                    </div>
                  </div>
                  {!soldOut && (
                    <div style={{ height: 3, background: 'rgba(255,255,255,0.05)', borderRadius: 2, overflow: 'hidden', marginTop: 14 }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: isSelected ? color : 'rgba(255,255,255,0.15)', transition: 'width 0.3s' }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT SIDE (CHECKOUT) */}
        <div className="checkout-panel" ref={summaryRef} style={{ position: 'sticky', top: 20, background: '#0A0D1A', padding: '24px 20px', borderRadius: 16, border: '1px solid rgba(0, 229, 255, 0.1)', height: 'fit-content' }}>
          <h3 style={{ margin: '0 0 24px', fontSize: 20, fontFamily: 'Bebas Neue', letterSpacing: '0.05em' }}>BOOKING SUMMARY</h3>

          <div style={{ marginBottom: 24 }}>
            {selCat ? (
              <div style={{ background: '#020205', padding: '16px', borderRadius: 8, border: `1px solid ${getCatColor(selectedCatIndex, cats.length)}40` }}>
                <div style={{ color: getCatColor(selectedCatIndex, cats.length), fontWeight: 'bold', fontFamily: 'Rajdhani', letterSpacing: '0.05em', fontSize: 14 }}>{selCat.name}</div>
                <div style={{ fontFamily: 'Bebas Neue', fontSize: 32, marginTop: 4 }}>₹{selCat.price?.toLocaleString()}</div>
              </div>
            ) : (
              <div style={{ color: 'rgba(255,255,255,0.4)', padding: '20px', textAlign: 'center', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 13 }}>
                Select a category from the map
              </div>
            )}
          </div>

          <div style={{ marginBottom: 24 }}>
            <div style={{ fontFamily: 'Rajdhani', fontSize: 12, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.5)', marginBottom: 10 }}>NUMBER OF TICKETS</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', background: '#020205', borderRadius: 8, padding: 4, border: '1px solid rgba(255,255,255,0.05)' }}>
                <button className="qty-btn" disabled={quantity <= 1} onClick={() => setQuantity(q => q - 1)}>-</button>
                <span style={{ width: 44, textAlign: 'center', fontSize: 22, fontFamily: 'Bebas Neue' }}>{quantity}</span>
                <button className="qty-btn" disabled={quantity >= 6} onClick={() => setQuantity(q => q + 1)}>+</button>
              </div>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>Max 6 per booking</span>
            </div>
          </div>

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 20, paddingBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 12 }}>
              <span>{quantity} × ₹{selCat?.price?.toLocaleString() || 0}</span>
              <span>₹{subtotal.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>
              <span>Convenience fee (2%)</span>
              <span>₹{fee.toLocaleString()}</span>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24 }}>
            <span style={{ fontSize: 18, fontFamily: 'Rajdhani', fontWeight: 'bold' }}>Total</span>
            <span style={{ fontFamily: 'Bebas Neue', fontSize: 36, color: '#00E5FF', lineHeight: 1 }}>₹{grandTotal.toLocaleString()}</span>
          </div>

          <button
            onClick={handleProceed}
            disabled={!selCat || holding}
            style={{ width: '100%', padding: '16px', background: selCat ? 'linear-gradient(90deg, #0055FF, #00E5FF)' : '#111', color: '#fff', border: 'none', borderRadius: 8, fontSize: 20, fontFamily: 'Bebas Neue', cursor: selCat ? 'pointer' : 'not-allowed', transition: 'all 0.2s' }}
          >
            {holding ? 'Processing...' : 'PROCEED TO BOOK'}
          </button>
        </div>
      </div>
    </div>
  );
}